import { DropdownOption } from "../features/auth/auth-state";
import { ProposalRidersList } from "../features/gms-proposal-forms/attachable-rider/attachable-riders.state";
import { getRiderId, PROPOSAL_PRODUCTS } from "../helper/proposal/proposal-constant-rider-id";

export interface ProposalRiderProductMap {
    [productAcronym: string]: number;
}

export interface ProposalRider {
    productFor: ProposalRiderProductMap;
    name: string;
    isActive: boolean;
    acronym: string;
    labelName: string;
}

export const PROPOSAL_AS_IDS = {
    new: 60,
    renew: 61
} as const;

export const COVERAGE_TYPE_IDS = {
    levelRanking: 32,
    uniformCoverage: 33,
    salaryRank: 34
} as const;

export const OTHERS_INPUT_IDS = {
    groupClassification: 5,
    businessType: 10,
    typeOfGroup: 17
} as const;

export const SUB_TYPE_IDS = {
    employerEmployee: 11,
    OFW: 16
} as const;

export const PROPOSAL_TYPE_IDS = {
    prototype: 30,
    customized: 31
} as const;

export const PRODUCT_TYPE_IDS = {
    GCLI: 1,
    GPA: 3,
    GYRT: 2,
    GCI: 4,
}

export const BASIC_PLAN_TYPE_IDS = {
    GCLIP: 1,
    GTLIP: 2,
    GADDP: 4,
    GADDPP: 5,
    GCIP_T5: 6,
    GCIP_T45: 7,
}

export const RATES_CONSTANTS = {
    maxNumberOfLive: 31,
    minAgeProfile: 18,
    maxAgeProfile: 65,
} as const;

export const LOAN_AMOUNTS_TYPE_IDS = {
    annually: 59,
    outstanding: 39,
    initial: 40,
} as const;

export const BASIC_PLAN_IDS = {
    GCLIP: {
        id: 1,
        name: 'Group Credit Life Insurance Plan',
        isActive: true,
        acronym: 'GCLIP',
        labelName: 'GCLIP'
    },
    GYRTP: {
        id: 2,
        name: 'Group Term Life Insurance Plan',
        isActive: true,
        acronym: 'GYRTP',
        labelName: 'GYRTP'
    },
    GCBPR: {
        id: 3,
        name: 'Group Comprehensive Benefits Plan (Retirement)',
        isActive: true,
        acronym: 'GCBPR',
        labelName: 'GCBPR'
    }, 
    GADDP: {
        id: 4,
        name: 'Group Accidental Death and Disability Plan',
        isActive: true,
        acronym: 'GADDP',
        labelName: 'GADDP'
    }, 
    GADDPP: {
        id: 5,
        name: 'Group Accidental Death and Disability Plus Plan',
        isActive: true,
        acronym: 'GADDPP',
        labelName: 'GADDPP'
    }, 
    GCI5P: {
        id: 6,
        name: 'Group Critical Illness Plan - Top 5',
        isActive: true,
        acronym: 'GCI5P',
        labelName: 'GCI5P'
    }, 
    GCI45P: {
        id: 7,
        name: 'Group Critical Illness Plan - Top 45',
        isActive: true,
        acronym: 'GCI45P',
        labelName: 'GCI45P'
    }, 
}

export const PROPOSAL_RIDERS = {
    GTPDRC: {
        productFor: {
            GCLI: 1,
        },
        name: 'Total and Permanent Disability Rider for Creditors',
        isActive: true,
        acronym: 'GTPDRC',
        labelName: 'GTPDRC',
    },

    GADBR: {
        productFor: {
            GYRT: 2,
            GCI: 22,
        },
        name: 'Group Accidental Death Benefit Rider',
        isActive: true,
        acronym: 'GADBR',
        labelName: 'GADBR',
    },

    GADDR: {
        productFor: {
            GYRT: 3,
            GCI: 23,
        },
        name: 'Group Accidental Death and Disability Rider',
        isActive: true,
        acronym: 'GADDR',
        labelName: 'GADDR',
    },

    GADDR_PLUS: {
        productFor: {
            GYRT: 4,
            GCI: 24,
        },
        name: 'Group Accidental Death and Disability Rider Plus',
        isActive: true,
        acronym: 'GADDR+',
        labelName: 'GADDR+',
    },

    GSADDR: {
        productFor: {
            GYRT: 5,
            GCI: 25,
        },
        name: 'Group Special Accidental Death and Disability Rider',
        isActive: true,
        acronym: 'GSADDR',
        labelName: 'GSADDR',
    },

    GTPDR: {
        productFor: {
            GYRT: 6,
            GCI: 26,
        },
        name: 'Group Total and Permanent Disability Rider',
        isActive: true,
        acronym: 'GTPDR',
        labelName: 'GTPDR',
    },

    GTIR: {
        productFor: {
            GYRT: 7,
            GCI: 27,
        },
        name: 'Group Terminal Illness Rider',
        isActive: true,
        acronym: 'GTIR',
        labelName: 'GTIR',
    },

    GHIR: {
        productFor: {
            GYRT: 8,
            GCI: 28,
        },
        name: 'Group Hospital Income Rider',
        isActive: true,
        acronym: 'GHIR',
        labelName: 'GHIR',
    },

    GAMERR: {
        productFor: {
            GYRT: 9,
            GPA: 14,
            GCI: 29,
        },
        name: 'Group Accidental Medical Expense Reimbursement Rider',
        isActive: true,
        acronym: 'GAMERR',
        labelName: 'GAMERR',
    },

    ALCR: {
        productFor: {
            GYRT: 10,
            GCI: 30,
        },
        name: 'Additional/Life Coverage',
        isActive: true,
        acronym: 'ALCR',
        labelName: 'ALCR',
    },

    BMSR: {
        productFor: {
            GYRT: 11,
            GCI: 31,
        },
        name: 'Burial (Memorial/Service)',
        isActive: true,
        acronym: 'BMSR',
        labelName: 'BMSR',
    },

    GMR: {
        productFor: {
            GYRT: 12,
            GPA: 15,
            GCI: 32,
        },
        name: 'Group Medical Rider',
        isActive: true,
        acronym: 'GMR',
        labelName: 'GMR',
    },

    GDR: {
        productFor: {
            GYRT: 13,
            GCI: 33,
        },
        name: 'Group Dengue Rider',
        isActive: true,
        acronym: 'GDR',
        labelName: 'GDR',
    },

    GMBDR: {
        productFor: {
            GYRT: 16,
            GPA: 19,
            GCI: 34,
        },
        name: 'Group Mosquito-Borne Disease Rider (Moskibite)',
        isActive: true,
        acronym: 'GMBDR',
        labelName: 'GMBDR',
    },

    GCIR5: {
        productFor: {
            GYRT: 17,
            GPA: 20,
        },
        name: 'Group Critical Illness Rider - Top 5',
        isActive: true,
        acronym: 'GCIR5',
        labelName: 'GCIR5',
    },

    GCIR45: {
        productFor: {
            GYRT: 18,
            GPA: 21,
        },
        name: 'Group Critical Illness Rider - Top 45',
        isActive: true,
        acronym: 'GCIR45',
        labelName: 'GCIR45',
    },
} as const;

export const PROPOSAL_RIDERS_LIST: ProposalRidersList[] = [
    {
        id: 1,
        name: 'Total and Permanent Disability Rider for Creditors',
        isActive: true,
        acronym: 'TPDRCR',
        labelName: 'TPDRCR',
    },
    {
        id: 2,
        name: 'Group Accidental Death Benefit Rider',
        isActive: true,
        acronym: 'GADBR',
        labelName: 'GADBR',
    },
    {
        id: 3,
        name: 'Group Accidental Death and Disability Rider',
        isActive: true,
        acronym: 'GADDR',
        labelName: 'GADDR',
    },
    {
        id: 4,
        name: 'Group Accidental Death and Disability Rider Plus',
        isActive: true,
        acronym: 'GADDRPR',
        labelName: 'GADDRPR',
    },
    {
        id: 5,
        name: 'Group Special Accidental Death and Disability Rider',
        isActive: true,
        acronym: 'GSADDR',
        labelName: 'GSADDR',
    },
    {
        id: 6,
        name: 'Group Total and Permanent Disability Rider',
        isActive: true,
        acronym: 'GTPDR',
        labelName: 'GTPDR',
    },
    {
        id: 7,
        name: 'Group Terminal Illness Rider',
        isActive: true,
        acronym: 'GTIR',
        labelName: 'GTIR',
    },
    {
        id: 8,
        name: 'Group Hospital Income Rider',
        isActive: true,
        acronym: 'GHIR',
        labelName: 'GHIR',
    },
    {
        id: 9,
        name: 'Group Accidental Medical Expense Reimbursement Rider',
        isActive: true,
        acronym: 'GAMERR',
        labelName: 'GAMERR_1',
    },
    {
        id: 10,
        name: 'Additional/Life Coverage',
        isActive: true,
        acronym: 'ALCR',
        labelName: 'ALCR',
    },
    {
        id: 11,
        name: 'Burial (Memorial/Service)',
        isActive: true,
        acronym: 'BMSR',
        labelName: 'BMSR',
    },
    {
        id: 12,
        name: 'Group Medical Rider',
        isActive: true,
        acronym: 'GMR',
        labelName: 'GMR_1',
    },
    {
        id: 13,
        name: 'Group Dengue Rider',
        isActive: true,
        acronym: 'GDR',
        labelName: 'GDR',
    },
    {
        id: 14,
        name: 'Group Accidental Medical Expense Reimbursement Rider',
        isActive: true,
        acronym: 'GAMERR',
        labelName: 'GAMERR_2',
    },
    {
        id: 15,
        name: 'Group Medical Rider',
        isActive: true,
        acronym: 'GMR',
        labelName: 'GMR_2',
    },
    {
        id: 16,
        name: 'Group Mosquito-Borne Disease Rider',
        isActive: true,
        acronym: 'GMBDR',
        labelName: 'GMBDR_1'
    },
    {
        id: 17,
        name: 'Group Critical Illness Rider - Top 5',
        isActive: true,
        acronym: 'GCI5R',
        labelName: 'GCI5R_1'
    },
    {
        id: 18,
        name: 'Group Critical Illness Rider - Top 45',
        isActive: true,
        acronym: 'GCI45R',
        labelName: 'GCI45R_1'
    },
    {
        id: 19,
        name: 'Group Mosquito-Borne Disease Rider',
        isActive: true,
        acronym: 'GMBDR',
        labelName: 'GMBDR_2'
    },
    {
        id: 20,
        name: 'Group Critical Illness Rider - Top 5',
        isActive: true,
        acronym: 'GCI5R',
        labelName: 'GCI5R_2'
    },
    {
        id: 21,
        name: 'Group Critical Illness Rider - Top 45',
        isActive: true,
        acronym: 'GCI45R',
        labelName: 'GCI45R_2'
    },
];

export const SINGLE_RIDER_IDS_COVERAGE: number[] = [
    getRiderId(PROPOSAL_RIDERS.GADBR, 'GYRT')!,
    getRiderId(PROPOSAL_RIDERS.GADDR, 'GYRT')!,
    getRiderId(PROPOSAL_RIDERS.GADDR_PLUS, 'GYRT')!,
    getRiderId(PROPOSAL_RIDERS.GSADDR, 'GYRT')!,
];

export const SINGLE_RIDER_IDS_GCIR: number[] = [
    getRiderId(PROPOSAL_RIDERS.GCIR5, 'GYRT')!,
    getRiderId(PROPOSAL_RIDERS.GCIR45, 'GYRT')!,
    getRiderId(PROPOSAL_RIDERS.GCIR5, 'GPA')!,
    getRiderId(PROPOSAL_RIDERS.GCIR45, 'GPA')!,
];

export const SINGLE_RIDER_IDS_GCI_TOP4: number[] = [
    getRiderId(PROPOSAL_RIDERS.GADBR, 'GCI')!,
    getRiderId(PROPOSAL_RIDERS.GADDR, 'GCI')!,
    getRiderId(PROPOSAL_RIDERS.GADDR_PLUS, 'GCI')!,
    getRiderId(PROPOSAL_RIDERS.GSADDR, 'GCI')!,
];

export const SINGLE_RIDER_IDS_GCI_DENGUE_MOSQUITO: number[] = [
    getRiderId(PROPOSAL_RIDERS.GDR, 'GCI')!,
    getRiderId(PROPOSAL_RIDERS.GMBDR, 'GCI')!,
];

export const SINGLE_RIDER_IDS_DISEASE: number[] = [
    getRiderId(PROPOSAL_RIDERS.GDR, 'GYRT')!,
    getRiderId(PROPOSAL_RIDERS.GMBDR, 'GYRT')!,
    getRiderId(PROPOSAL_RIDERS.GMBDR, 'GPA')!,
];

export const AVAILABLE_RIDER_ID_FOR_GCLI: number[] = [
    getRiderId(PROPOSAL_RIDERS.GTPDRC, 'GCLI')!,
];

export const AVAILABLE_RIDER_ID_FOR_GPA: number[] = [
    getRiderId(PROPOSAL_RIDERS.GAMERR, 'GPA')!,
    getRiderId(PROPOSAL_RIDERS.GMR, 'GPA')!,
    getRiderId(PROPOSAL_RIDERS.GMBDR, 'GPA')!,
    getRiderId(PROPOSAL_RIDERS.GCIR5, 'GPA')!,
    getRiderId(PROPOSAL_RIDERS.GCIR45, 'GPA')!,
];

export const AVAILABLE_RIDER_ID_FOR_GYRT: number[] = [
    getRiderId(PROPOSAL_RIDERS.GADBR, 'GYRT')!,
    getRiderId(PROPOSAL_RIDERS.GADDR, 'GYRT')!,
    getRiderId(PROPOSAL_RIDERS.GADDR_PLUS, 'GYRT')!,
    getRiderId(PROPOSAL_RIDERS.GSADDR, 'GYRT')!,
    getRiderId(PROPOSAL_RIDERS.GTPDR, 'GYRT')!,
    getRiderId(PROPOSAL_RIDERS.GTIR, 'GYRT')!,
    getRiderId(PROPOSAL_RIDERS.GHIR, 'GYRT')!,
    getRiderId(PROPOSAL_RIDERS.GAMERR, 'GYRT')!,
    getRiderId(PROPOSAL_RIDERS.ALCR, 'GYRT')!,
    getRiderId(PROPOSAL_RIDERS.BMSR, 'GYRT')!,
    getRiderId(PROPOSAL_RIDERS.GMR, 'GYRT')!,
    getRiderId(PROPOSAL_RIDERS.GDR, 'GYRT')!,
    getRiderId(PROPOSAL_RIDERS.GMBDR, 'GYRT')!,
    getRiderId(PROPOSAL_RIDERS.GCIR5, 'GYRT')!,
    getRiderId(PROPOSAL_RIDERS.GCIR45, 'GYRT')!,
];

export const defaultAmountByRider: Record<number, number> = {
    [getRiderId(PROPOSAL_RIDERS.GDR, 'GYRT')!]: 0,
    [getRiderId(PROPOSAL_RIDERS.GDR, 'GCI')!]: 0,

    [getRiderId(PROPOSAL_RIDERS.GHIR, 'GYRT')!]: 100,
    [getRiderId(PROPOSAL_RIDERS.GHIR, 'GCI')!]: 100,

    [getRiderId(PROPOSAL_RIDERS.GAMERR, 'GYRT')!]: 500,
    [getRiderId(PROPOSAL_RIDERS.GAMERR, 'GPA')!]: 500,
    [getRiderId(PROPOSAL_RIDERS.GAMERR, 'GCI')!]: 500,

    [getRiderId(PROPOSAL_RIDERS.GMBDR, 'GYRT')!]: 10000,
    [getRiderId(PROPOSAL_RIDERS.GMBDR, 'GPA')!]: 10000,
    [getRiderId(PROPOSAL_RIDERS.GMBDR, 'GCI')!]: 10000,
};

export const proposalHeaderData = {
    proposalStatus: { id: 1, label: 'PROPOSAL STATUS' },
    policyStatus: { id: 2, label: 'POLICY STATUS' },
    ratesStatus: { id: 3, label: 'RATES STATUS' },
    proposalAs: { id: 4, label: 'PROPOSAL AS' },
    groupName: { id: 5, label: 'GROUP NAME' },
    email: { id: 6, label: 'EMAIL' },
    proposalType: { id: 7, label: 'PROPOSAL TYPE' },
    product: { id: 8, label: 'PRODUCT' },
    preparedBy: { id: 9, label: 'PREPARED BY' },
    ratesBy: { id: 10, label: 'RATES BY' },
    endorsedBy: { id: 11, label: 'ENDORSED BY' },
    endorsedTo: { id: 12, label: 'ENDORSED TO' },
    action: { id: 13, label: 'ACTION' },
    computedBy: { id: 14, label: 'COMPUTED BY' },

    status: { id: 15, label: 'STATUS' },
    notes: { id: 16, label: 'NOTES' },
    cocAction: { id: 17, label: 'ACTION' },
    policyNumber: { id: 18, label: 'POLICY NUMBER' },
    conflictProposalAction: { id: 19, label: 'ACTION' },
    basicPlan: { id: 20, label: 'BASIC PLAN' },
    effectiveDate: { id: 21, label: 'EFFECTIVE DATE' },
    renewedBy: { id: 22, label: 'RENEWED BY' },
} as const;

export const proposalHeaders: string[] = [
    proposalHeaderData.proposalStatus.label,
    proposalHeaderData.policyStatus.label,
    proposalHeaderData.ratesStatus.label,
    proposalHeaderData.proposalAs.label,
    proposalHeaderData.groupName.label,
    proposalHeaderData.email.label,
    proposalHeaderData.proposalType.label,
    proposalHeaderData.product.label,
    proposalHeaderData.preparedBy.label,
    proposalHeaderData.ratesBy.label,
    proposalHeaderData.endorsedBy.label,
    proposalHeaderData.computedBy.label,
    proposalHeaderData.endorsedTo.label,
    proposalHeaderData.action.label,
    proposalHeaderData.status.label,
    proposalHeaderData.notes.label,
];

export const userHeaders: string[] = [
    'STATUS',
    'NAME',
    'EMAIL',
    'AGENT CODE',
    'POSITION',
    'DEPARTMENT',
    'ROLE',
    'REPORTING HEAD',
];

export const proposalComputationHeaders: string[] = [
    'STATUS',
    'GROUP NAME',
    'EMAIL',
    'PRODUCT',
    'PREPARED BY',
    'ACTION'
];

export const CHANNEL_TYPE_ID = {
    select: 0,
    direct: 55,
    broker: 56,
    generalAgency: 57,
    agent: 58,
} as const;

export const PROPOSAL_AS_ID = {
    select: 0,
    new: 60,
    renew: 61
} as const;

export const PROPOSAL_STATUS = {
    itDepartment: {
        id: 1,
        value: 'it department',
        type: 'DEPARTMENT'
    },

    actuarialDepartment: {
        id: 2,
        value: 'actuarial department',
        type: 'DEPARTMENT'
    },

    ebamDepartment: {
        id: 3,
        value: 'ebam department',
        type: 'DEPARTMENT'
    },

    gmsDepartment: {
        id: 4,
        value: 'gms department',
        type: 'DEPARTMENT'
    },

    open: {
        id: 5,
        value: 'open',
        type: 'GMS'
    },

    closed: {
        id: 6,
        value: 'closed',
        type: 'GMS'
    },

    booked: {
        id: 7,
        value: 'booked',
        type: 'GMS'
    },

    pending: {
        id: 8,
        value: 'pending',
        type: 'GMS'
    },

    rejected: {
        id: 9,
        value: 'rejected',
        type: 'GMS'
    },

    amendGMS: {
        id: 10,
        value: 'amend',
        type: 'GMS'
    },

    draft: {
        id: 11,
        value: 'draft',
        type: 'GMS'
    },

    onHold: {
        id: 12,
        value: 'on-hold',
        type: 'GMS'
    },

    rating: {
        id: 13,
        value: 'rating',
        type: 'ACTUARIAL'
    },

    approved: {
        id: 14,
        value: 'approved',
        type: 'ACTUARIAL'
    },

    released: {
        id: 15,
        value: 'released',
        type: 'ACTUARIAL'
    },

    amendActuarial: {
        id: 16,
        value: 'amend',
        type: 'ACTUARIAL'
    },

    rejectedActuarial: {
        id: 17,
        value: 'rejected',
        type: 'ACTUARIAL'
    },

    forContractCreation: {
        id: 18,
        value: 'for contract creation',
        type: 'EBAM'
    },

    contractInProgress: {
        id: 19,
        value: 'contract in progress',
        type: 'EBAM'
    },

    forContractReview: {
        id: 20,
        value: 'for contract review',
        type: 'EBAM'
    },

    forRevision: {
        id: 21,
        value: 'for revision',
        type: 'EBAM'
    },

    contractApproved: {
        id: 22,
        value: 'contract approved',
        type: 'EBAM'
    },

    readyForIssuance: {
        id: 23,
        value: 'ready for issuance',
        type: 'EBAM'
    },

    issued: {
        id: 24,
        value: 'issued',
        type: 'EBAM'
    },

    denied: {
        id: 25,
        value: 'denied',
        type: 'ACTUARIAL'
    },

    queued: {
        id: 26,
        value: 'queued',
        type: 'ACTUARIAL'
    },

    endorsed: {
        id: 27,
        value: 'endorsed',
        type: 'ACTUARIAL'
    },

    forApproval: {
        id: 28,
        value: 'for approval',
        type: 'ACTUARIAL'
    },

    passed: {
        id: 29,
        value: 'passed',
        type: 'ACTUARIAL'
    },

    forReapproval: {
        id: 30,
        value: 'for reapproval',
        type: 'ACTUARIAL'
    },
    amendingActuarial: {
        id: 17,
        value: 'amending',
        type: 'ACTUARIAL'
    },
} as const;

export const STATUS_STYLES: Record<string, string> = {
    [PROPOSAL_STATUS.itDepartment.value]:
        'border-slate-200 bg-slate-50 text-slate-600',

    [PROPOSAL_STATUS.actuarialDepartment.value]:
        'border-indigo-200 bg-indigo-50 text-indigo-700',

    [PROPOSAL_STATUS.ebamDepartment.value]:
        'border-violet-200 bg-violet-50 text-violet-700',

    [PROPOSAL_STATUS.gmsDepartment.value]:
        'border-[#186cb2]/20 bg-[#186cb2]/5 text-[#186cb2]',

    [PROPOSAL_STATUS.open.value]:
        'border-[#186cb2]/20 bg-[#186cb2]/5 text-[#186cb2]',

    [PROPOSAL_STATUS.closed.value]:
        'border-gray-300 bg-gray-100 text-gray-700',

    [PROPOSAL_STATUS.booked.value]:
        'border-blue-200 bg-blue-50 text-blue-700',

    [PROPOSAL_STATUS.pending.value]:
        'border-yellow-200 bg-yellow-50 text-yellow-700',

    [PROPOSAL_STATUS.rejected.value]:
        'border-red-200 bg-red-50 text-red-700',

    [PROPOSAL_STATUS.amendGMS.value]:
        'border-orange-200 bg-orange-50 text-orange-700',

    [PROPOSAL_STATUS.draft.value]:
        'border-gray-200 bg-gray-100 text-gray-600',

    [PROPOSAL_STATUS.onHold.value]:
        'border-amber-200 bg-amber-50 text-amber-700',

    [PROPOSAL_STATUS.rating.value]:
        'border-cyan-200 bg-cyan-50 text-cyan-700',

    [PROPOSAL_STATUS.approved.value]:
        'border-green-200 bg-green-50 text-green-700',

    [PROPOSAL_STATUS.released.value]:
        'border-purple-200 bg-purple-50 text-purple-700',

    [PROPOSAL_STATUS.forContractCreation.value]:
        'border-sky-200 bg-sky-50 text-sky-700',

    [PROPOSAL_STATUS.contractInProgress.value]:
        'border-blue-200 bg-blue-50 text-blue-700',

    [PROPOSAL_STATUS.forContractReview.value]:
        'border-indigo-200 bg-indigo-50 text-indigo-700',

    [PROPOSAL_STATUS.forRevision.value]:
        'border-orange-200 bg-orange-50 text-orange-700',

    [PROPOSAL_STATUS.contractApproved.value]:
        'border-green-200 bg-green-50 text-green-700',

    [PROPOSAL_STATUS.readyForIssuance.value]:
        'border-teal-200 bg-teal-50 text-teal-700',

    [PROPOSAL_STATUS.issued.value]:
        'border-emerald-200 bg-emerald-50 text-emerald-700',

    [PROPOSAL_STATUS.denied.value]:
        'border-red-200 bg-red-50 text-red-700',

    [PROPOSAL_STATUS.queued.value]:
        'border-gray-200 bg-gray-50 text-gray-700',

    [PROPOSAL_STATUS.endorsed.value]:
        'border-blue-200 bg-blue-50 text-blue-700',

    [PROPOSAL_STATUS.forApproval.value]:
        'border-yellow-200 bg-yellow-50 text-yellow-700',

    [PROPOSAL_STATUS.passed.value]:
        'border-green-200 bg-green-50 text-green-700',

    [PROPOSAL_STATUS.forReapproval.value]:
        'border-purple-200 bg-purple-50 text-purple-700',
};

export const MAX_AGE = 65;
export const MIN_AGE = 18;

export const companyId = {
    tinNumber: {
        id: 64
    },
    secNumber: {
        id: 65
    }
}

export const contactNumberId = {
    telephone: {
        id: 67
    },
    mobile: {
        id: 66
    }
}

export const companyTypeOfId:DropdownOption[]  = [
    {id: 1, name: 'TIN', subTitle: '', value: 0},
    {id: 2, name: 'SEC', subTitle: '', value: 0}
]

export const companyTypeOfNumber:DropdownOption[]  = [
    {id: 1, name: 'Mobile', subTitle: '', value: 0},
    {id: 2, name: 'Telephone', subTitle: '', value: 0}
]

export const contactPersonTypeOfNumber:DropdownOption[]  = [
    {id: 1, name: 'Mobile', subTitle: '', value: 0},
    {id: 2, name: 'Telephone', subTitle: '', value: 0}
]

export const typeOfSuffix:DropdownOption[]  = [
    {id: 1, name: 'Select', subTitle: '', value: 0},
    {id: 2, name: 'Mr.', subTitle: '', value: 0},
    {id: 3, name: 'Mrs.', subTitle: '', value: 0},
    {id: 4, name: 'Ms.', subTitle: '', value: 0},
    {id: 5, name: 'Dr.', subTitle: '', value: 0},
    {id: 6, name: 'Prof.', subTitle: '', value: 0},
    {id: 7, name: 'Hon.', subTitle: '', value: 0},
    {id: 8, name: 'Atty.', subTitle: '', value: 0},
    {id: 9, name: 'Engr.', subTitle: '', value: 0}
]